<template>
  <div class="job-offer-list container">
    <h2>Job Offers</h2>

    <data-table
      ref="dataTable"
      :row-selection="false"
      :show-filters="true"
      :show-pager="true"
      :items="filteredJobOffers"
      :filters="filters"
      :filter-title="'Filter Studies'"
      :fields="fields"
      :busy="tableBusy"
    >
      <!-- template for filters -->
      <template slot="filters">
        <b-row class="px-2">
          <b-col lg="6" md="12" class="mb-2">
            <label>Email</label>
            <b-form-input v-model="filters.email.value" />
          </b-col>
          <b-col lg="6" md="12" class="mb-2">
            <label>Company</label>
            <b-form-input v-model="filters.company_name.value" />
          </b-col>
        </b-row>
      </template>
      <template slot="actions" slot-scope="data">
        <b-dropdown
          split
          variant="info"
          size="sm"
          text="Edit"
          @click.stop="onEditJobOffer(data.item.id)"
        >
          <b-dropdown-item-button
            v-if="data.item.status === jobOfferStatuses.ACTIVE"
            @click.stop="onUnpostJobOffer(data.item.id)"
            class="btn-sm"
            :disabled="!!loadingUnpostJobOfferId"
          >
            <b-spinner v-if="loadingUnpostJobOfferId === data.item.id" small />
            <span v-else>Unpost</span>
          </b-dropdown-item-button>
          <b-dropdown-item-button
            @click.stop="onDeleteJobOffer(data.item.id)"
            class="btn-sm"
            :disabled="!!loadingDeleteJobOfferId"
          >
            <b-spinner v-if="loadingDeleteJobOfferId === data.item.id" small />
            <span v-else>Delete</span>
          </b-dropdown-item-button>
        </b-dropdown>
      </template>
    </data-table>
    <modal-edit-form
      v-if="showModal"
      :initial-job-offer="editedJobOffer"
      :key="'jomef' + editedJobOffer.id"
      ref="modal"
      @modal-cancel="onModalCancel"
      @job-offer-saved="onJobOfferSaved"
    />
  </div>
</template>

<script>
import DataTable from '@/components/DataTable';
import Api from '@/providers/Api';
import {
  handleServerErrorWithValidation,
  showValidationErrorsAsToasted,
} from '@/helpers/ToastedHelper';
import ModalEditForm from '@/components/gm_admin/job-offers/ModalEditForm';
import { JOB_OFFER_STATUSES } from '@/store';
import Help from '@/providers/Help';

export default {
  name: 'JobOffersList',
  components: {
    DataTable,
    ModalEditForm,
  },
  data: function () {
    return {
      jobOffers: [],
      editedJobOffer: null,
      showModal: false,
      tableBusy: false,
      filters: {
        email: { field: 'email', type: 'string', value: null },
        company_name: { field: 'company_name', type: 'string', value: null },
      },
      fields: [
        { key: 'id', sortable: true },
        { key: 'userEmail', sortable: true, label: 'Email' },
        { key: 'title', sortable: true, label: 'Title' },
        {
          key: 'company_name',
          sortable: false,
          label: 'Company',
        },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'posted_date', sortable: true, label: 'Posted Date' },
        { key: 'actions', sortable: false, custom: true },
      ],
      loadingUnpostJobOfferId: null,
      loadingDeleteJobOfferId: null,
      jobOfferStatuses: JOB_OFFER_STATUSES,
    };
  },
  created: function () {
    this.getJobOffers();
  },
  computed: {
    filteredJobOffers: function () {
      const filterByEmail = this.filters.email && !!this.filters.email.value;
      const filterByCompanyName =
        this.filters.company_name && !!this.filters.company_name.value;
      if (filterByEmail || filterByCompanyName) {
        return this.jobOffers.filter((jo) => {
          let isMatch = true;
          if (filterByEmail) {
            isMatch &= jo.userEmail
              .toLowerCase()
              .includes(this.filters.email.value.toLowerCase());
          }
          if (filterByCompanyName) {
            isMatch &= jo.company_name && jo.company_name
              .toLowerCase()
              .includes(this.filters.company_name.value.toLowerCase());
          }
          return isMatch;
        });
      } else return this.jobOffers;
    },
  },
  methods: {
    getJobOffers: async function () {
      this.tableBusy = true;
      try {
        this.jobOffers = await Api.allJobOffersForAdmin();
      } catch (e) {
        handleServerErrorWithValidation(e, this.$toasted);
      } finally {
        this.tableBusy = false;
      }
    },
    onDeleteJobOffer: function (id) {
      Help.confirm.show('Permanently delete this Job Offer?', 'Delete', () => {
        this.deleteJobOffer(id);
      });
    },
    deleteJobOffer: async function (id) {
      this.loadingDeleteJobOfferId = id;
      try {
        await Api.deleteJobOffer(id);
        const index = this.jobOffers.findIndex((q) => q.id === id);
        this.jobOffers.splice(index, 1);
        this.$toasted.success('Job Offer successfully deleted.', {
          duration: 3000,
        });
        this.$refs.dataTable.refresh();
      } catch (e) {
        this.showErrors(e);
      } finally {
        this.loadingDeleteJobOfferId = null;
      }
    },
    showErrors: function (error) {
      if (
        error &&
        error.response &&
        error.response.data &&
        error.response.data.errors
      ) {
        showValidationErrorsAsToasted(
          this.$toasted,
          error.response.data.errors
        );
      } else {
        this.$toasted.error(error.response.data.message, { duration: 3000 });
      }
    },
    onUnpostJobOffer: async function (id) {
      this.loadingUnpostJobOfferId = id;
      try {
        const jobOffer = await Api.unpostJobOffer(id);
        this.$toasted.success('Job Offer successfully unposted.', {
          duration: 3000,
        });
        this.onJobOfferSaved(jobOffer);
      } catch (e) {
        this.showErrors(e, this.$toasted);
      } finally {
        this.loadingUnpostJobOfferId = null;
      }
    },
    onEditJobOffer: function (id) {
      const jobOffer = this.jobOffers.find((jO) => jO.id === id);
      this.openEditModal(jobOffer);
    },
    openEditModal: function (jobOffer) {
      this.editedJobOffer = jobOffer;
      this.showModal = true;
    },
    onModalCancel: function () {
      this.editedJobOffer = null;
      this.showModal = false;
    },
    onJobOfferSaved: function (jobOffer) {
      const index = this.jobOffers.findIndex((jO) => jO.id === jobOffer.id);
      if (index !== -1) {
        this.$set(this.jobOffers, index, jobOffer);
      } else {
        this.$set(this.jobOffers, this.jobOffers.length, jobOffer);
      }
    },
  },
};
</script>

<style scoped />
